<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import {AlertTriangle} from 'lucide-vue-next'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import ModelCombobox from './ModelCombobox.vue'
import {sortReasoningEfforts, type ModelRoleConfig, type ReasoningEffort} from '@shared/llm_types'
import {formatModelPrice} from '@/lib/model-pricing'

const props = defineProps<{role: ModelRoleConfig}>()
const emit = defineEmits<{change: [model: string | null, reasoning: ReasoningEffort | null]}>()
const model = ref(props.role.model)
const reasoning = ref<ReasoningEffort | null>(props.role.reasoning)
watch(() => props.role, (value) => { model.value = value.model; reasoning.value = value.reasoning })
watch([model, reasoning], () => emit('change', model.value, reasoning.value))
const selected = computed(() => props.role.compatibleModels.find((item) => item.id === model.value))
const reasoningOptions = computed(() => sortReasoningEfforts(selected.value?.reasoning?.supported_efforts || []))
function changeModel(value: string | null): void {
  model.value = value
  reasoning.value = reasoningOptions.value[0] || null
}
</script>

<template>
  <div class="grid gap-2 border-b px-2 py-2 last:border-b-0 lg:grid-cols-[minmax(10rem,1fr)_minmax(16rem,2fr)_12rem_9rem] lg:items-center">
    <div class="min-w-0"><Label class="break-words">{{ $t(`cp.roles.${role.role}`) }}</Label></div>
    <div class="min-w-0"><ModelCombobox :model="model" :options="role.compatibleModels" :missing="role.missingModel" @select="changeModel" /><p v-if="role.missingModel" class="mt-1 flex items-center gap-1 text-xs text-destructive"><AlertTriangle class="size-3" />Missing from catalog</p></div>
    <div class="text-xs tabular-nums text-muted-foreground"><template v-if="selected">{{ formatModelPrice(selected.pricing.prompt) }} / {{ formatModelPrice(selected.pricing.completion) }}</template><template v-else>—</template></div>
    <div v-if="reasoningOptions.length" class="min-w-0"><Select v-model="reasoning"><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem v-for="item in reasoningOptions" :key="item" :value="item">{{ item }}</SelectItem></SelectContent></Select></div>
    <div v-else />
  </div>
</template>
